import { Link, usePage } from "@inertiajs/react"
import { Lock, Mail, Monitor, Palette, User } from "lucide-react"
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import Heading from "@/components/heading"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  settingsAppearancePath,
  settingsEmailPath,
  settingsPasswordPath,
  settingsProfilePath,
  settingsSessionsPath,
} from "@/routes"
import type { NavItem } from "@/types"

const sidebarNavItems: NavItem[] = [
  {
    title: "Profile",
    href: settingsProfilePath(),
    icon: User,
  },
  {
    title: "Email",
    href: settingsEmailPath(),
    icon: Mail,
  },
  {
    title: "Password",
    href: settingsPasswordPath(),
    icon: Lock,
  },
  {
    title: "Sessions",
    href: settingsSessionsPath(),
    icon: Monitor,
  },
  {
    title: "Appearance",
    href: settingsAppearancePath(),
    icon: Palette,
  },
]

export default function SettingsLayout({ children }: PropsWithChildren) {
  const { url } = usePage()
  const navRef = useRef<HTMLElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollFades = useCallback(() => {
    const nav = navRef.current
    if (!nav) return
    setCanScrollLeft(nav.scrollLeft > 2)
    setCanScrollRight(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 2)
  }, [])

  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const active = nav.querySelector<HTMLElement>("[data-active]")
    if (active) {
      active.scrollIntoView({ inline: "center", block: "nearest" })
    }
    // Check fades after scroll-into-view settles
    requestAnimationFrame(updateScrollFades)
  }, [url, updateScrollFades])

  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    nav.addEventListener("scroll", updateScrollFades, { passive: true })
    updateScrollFades()
    return () => nav.removeEventListener("scroll", updateScrollFades)
  }, [updateScrollFades])

  return (
    <div className="px-4 py-6">
      <Heading
        title="Settings"
        description="Manage your profile and account settings"
      />

      <div className="flex flex-col space-y-8 lg:flex-row lg:space-y-0 lg:space-x-12">
        {/* Mobile: horizontal scrolling pills | Desktop: vertical sidebar */}
        <aside className="w-full lg:w-48">
          {/* Mobile pill nav */}
          <div
            className={cn(
              "settings-pill-scroll lg:hidden",
              canScrollLeft && "can-scroll-left",
              canScrollRight && "can-scroll-right",
            )}
          >
            <nav ref={navRef} className="settings-pill-nav">
              {sidebarNavItems.map((item) => {
                const isActive = url === item.href
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    {...(isActive ? { "data-active": true } : {})}
                    className={cn(
                      "settings-pill",
                      isActive && "settings-pill-active",
                    )}
                  >
                    {item.icon && <item.icon size={14} strokeWidth={2} />}
                    {item.title}
                  </Link>
                )
              })}
            </nav>
          </div>

          {/* Desktop sidebar nav */}
          <nav className="hidden flex-col space-y-1 lg:flex">
            {sidebarNavItems.map((item) => (
              <Button
                key={item.href}
                size="sm"
                variant="ghost"
                asChild
                className={cn("w-full justify-start", {
                  "bg-muted": url === item.href,
                })}
              >
                <Link href={item.href}>
                  {item.icon && <item.icon className="h-4 w-4" />}
                  {item.title}
                </Link>
              </Button>
            ))}
          </nav>
        </aside>

        <div className="flex-1 md:max-w-2xl">
          <section className="max-w-xl space-y-12">{children}</section>
        </div>
      </div>
    </div>
  )
}
